import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle } from 'lucide-react';

interface MembershipType {
  id: string;
  name: string;
  price_eur: number;
  description: string;
  benefits: string[];
}

const MembershipSignup = () => {
  const [membershipTypes, setMembershipTypes] = useState<MembershipType[]>([]);
  const [selectedMembership, setSelectedMembership] = useState<MembershipType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { type } = useParams();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    organization: '',
    position: '',
    phone: '',
    country: '',
    membershipTypeId: ''
  });

  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  useEffect(() => {
    if (membershipTypes.length > 0 && type) {
      // Map URL parameter to membership type
      const typeMap: { [key: string]: string } = {
        'student-member-signup': 'Student Member',
        'academic-member-signup': 'Academic Member',
        'professional-member-signup': 'Professional Member',
        'chartered-member-signup': 'Chartered Member',
        'training-centre-signup': 'Training Centre',
        'corporate-bronze-signup': 'Corporate Bronze',
        'corporate-silver-signup': 'Corporate Silver',
        'corporate-gold-signup': 'Corporate Gold'
      };

      const membershipName = typeMap[type];
      const membership = membershipTypes.find(m => m.name === membershipName);
      if (membership) {
        setSelectedMembership(membership);
        setFormData(prev => ({ ...prev, membershipTypeId: membership.id }));
      }
    }
  }, [membershipTypes, type]);

  const fetchMembershipTypes = async () => {
    const { data, error } = await supabase
      .from('membership_types_2025_12_03_10_30')
      .select('*')
      .order('price_eur', { ascending: true });
    
    if (error) {
      console.error('Error fetching membership types:', error);
    } else {
      setMembershipTypes(data || []);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!formData.membershipTypeId) {
      setError('Please select a membership type');
      setLoading(false);
      return;
    }

    try {
      // Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/#/auth/callback`,
          data: {
            full_name: formData.fullName
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Try to create/update profile, but don't fail if it doesn't work
        try {
          const { data: profileData, error: profileError } = await supabase.functions.invoke(
            'create_member_profile_2025_12_03_10_30',
            {
              body: {
                membershipData: {
                  membershipTypeId: formData.membershipTypeId,
                  fullName: formData.fullName,
                  organization: formData.organization,
                  position: formData.position,
                  phone: formData.phone,
                  country: formData.country
                }
              }
            }
          );

          if (profileError) {
            console.log('Profile creation will be handled later:', profileError);
          }
        } catch (error) {
          console.log('Profile creation will be handled later:', error);
        }

        setSuccess(true);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <CardTitle className="text-2xl">Application Submitted!</CardTitle>
            <CardDescription>
              Thank you for applying to join CSI. Please check your email for verification instructions.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Your membership application is now under review. You will receive an email confirmation once approved.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/member-login')}>
                Go to Login
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Membership Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-primary" />
                <div>
                  <CardTitle className="text-2xl">
                    {selectedMembership?.name || 'CSI Membership'}
                  </CardTitle>
                  <CardDescription>
                    {selectedMembership ? `€${selectedMembership.price_eur}/year` : 'Choose your membership level'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedMembership ? (
                <div className="space-y-4">
                  <p className="text-muted-foreground">{selectedMembership.description}</p>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Benefits Include:</h4>
                    <ul className="space-y-2">
                      {selectedMembership.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-primary/10 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Annual Fee:</span>
                      <span className="text-2xl font-bold text-primary">
                        €{selectedMembership.price_eur}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Select a membership type to see details</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Signup Form */}
          <Card>
            <CardHeader>
              <CardTitle>Join CSI</CardTitle>
              <CardDescription>
                Create your account and apply for membership
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Membership Type Selection */}
                {!selectedMembership && (
                  <div className="space-y-2">
                    <Label htmlFor="membershipType">Membership Type</Label>
                    <Select 
                      value={formData.membershipTypeId} 
                      onValueChange={(value) => {
                        handleInputChange('membershipTypeId', value);
                        const membership = membershipTypes.find(m => m.id === value);
                        setSelectedMembership(membership || null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select membership type" />
                      </SelectTrigger>
                      <SelectContent>
                        {membershipTypes.map((membership) => (
                          <SelectItem key={membership.id} value={membership.id}>
                            {membership.name} - €{membership.price_eur}/year
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Account Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Account Information</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Create password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Personal Information</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization</Label>
                      <Input
                        id="organization"
                        placeholder="Company/Institution"
                        value={formData.organization}
                        onChange={(e) => handleInputChange('organization', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="position">Position</Label>
                      <Input
                        id="position"
                        placeholder="Job title"
                        value={formData.position}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        placeholder="Phone number"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        placeholder="Country"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !selectedMembership}
                >
                  {loading ? 'Submitting Application...' : 'Submit Application'}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Button variant="link" className="p-0" onClick={() => navigate('/member-login')}>
                    Sign in here
                  </Button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MembershipSignup;